import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { Divider, message, Input } from "antd";
import { clearLocalStorageExcept } from "src/utils/HelperFunctions";
import {
  signupUser,
  handleGoogleOAuthCallback,
  getGoogleLoginUrl,
  validateEmail,
  validatePassword,
  redirectBasedOnStatus,
} from "../../services/AuthenticationAPIs";

const SignUpContent = () => {
  const navigate = useNavigate();

  const [values, setValues] = useState({ email: "", password: "" });
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  useEffect(() => {
    redirectBasedOnStatus(navigate);
  }, [navigate]);

  useEffect(() => {
    const handleOAuthCallback = async () => {
      const params = new URLSearchParams(window.location.search);
      const code = params.get("code");
      const state = params.get("state");

      if (code && state) {
        try {
          const data = await handleGoogleOAuthCallback(code, state);
          if (data.status) {
            message.success(data.message || "Google login successful.");
          } else {
            message.error(data.message || "Google login failed.");
          }
        } catch {
          message.error(
            "An error occurred during Google login. Please try again."
          );
        }
      }
    };

    handleOAuthCallback();
  }, []);

  const validateInputs = (): boolean => {
    const emailErr = validateEmail(values.email);
    const passwordErr = validatePassword(values.password);

    setEmailError(emailErr);
    setPasswordError(passwordErr);

    return !emailErr && !passwordErr;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    clearLocalStorageExcept(["admin_token", "admin_name"]);

    if (!validateInputs()) {return;}

    setIsSubmitting(true);
    setIsLoading(true);

    try {
      const data = await signupUser(values.email, values.password);

  if (data.status) {
  message.success(data.message || "Signup successful.");
  localStorage.setItem("api_key", data.api_key);
  localStorage.setItem("user_id", data.user_id);
  localStorage.setItem("tenant_id", data.tenant_id);
  localStorage.setItem("role_id", data.role_id);
  localStorage.setItem("email", data.email);
  navigate("/verify-otp" , { state: { from: "sign-up" } });
} else {
  const errorMsg =
    data.message || data.error || "Signup failed. Please try again.";
  message.error(errorMsg);
}
    }  catch (error: any) {
  const backendMessage =
    error?.response?.data?.message ||
    error?.message ||
    "Something went wrong. Please try again later.";
  message.error(backendMessage);
} finally {
      setIsSubmitting(false);
      setIsLoading(false);
    }
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setValues({ ...values, [name]: value });
  };

  const handleGoogleLogin = () => {
    clearLocalStorageExcept(["admin_token", "admin_name"]);
    try {
      window.location.href = getGoogleLoginUrl();
    } catch {
      message.error("Failed to initiate Google Login. Please try again.");
    }
  };

  return (
    <div className="body" style={{ backgroundImage: `url(/images/DatatramSignup.png)` }}>
      <img src="/images/DpLogo.png" className="logo-design" alt="logo" />
      <div className="app">
        <div className="signup-internal-container">
          <form className="formdesign" onSubmit={handleSubmit} noValidate>
            <h2 className="signup-heading">
              Meaningful insights to grow <br /> your business
            </h2>
            <br />
            <button
              onClick={handleGoogleLogin}
              className="custom-google-button"
              type="button"
            >
              <img
                src="/images/google-logo.svg"
                alt="Google logo"
              className="signup-google-logo"
              />
              Sign up with Google
            </button>
            <br />
            <Divider className="or">OR</Divider>
            <br />
            <div className="input-container">
              <label>Email Address</label>
              <Input
                name="email"
                className="input-email-password input-style-2"
                placeholder="Email Address"
                value={values.email}
                onChange={onChange}
                required
              />
              {emailError && (
                <span className="error-message">{emailError}</span>
              )}
            </div>
            <div className="input-container">
              <label>Create Password</label>
              <Input.Password
                name="password"
                className="input-email-password input-style-2"
                placeholder="Create Password"
                value={values.password}
                onChange={onChange}
                required
                disabled={isLoading}
              />
              {passwordError && (
                <span className="error-message">{passwordError}</span>
              )}
            </div>
            <button
              type="submit"
              className="button-style"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Creating Account..." : "Create Account"}
            </button>
            <p className="signup-page-text alreadyRegistered_text">
              Already have an account?&nbsp;
              <a href="/log-in">Log In</a>
            </p>
            <p className="signup-page-text viewTerms_text">
              By creating an account, you agree to our &nbsp;
              <a href="/privacy-policy">Terms and Conditions</a>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

const SignUp = () => (
  <GoogleOAuthProvider clientId="996633909190-2nnsu9ssi3kmjtkct4dsvt9e7hdgn1fj.apps.googleusercontent.com">
    <SignUpContent />
  </GoogleOAuthProvider>
);

export default SignUp;
